import React, { useState } from "react";
import Page from "components/layouts/Page";
import {
  ApiException,
  BaselineDefaultsIncentivesForm,
  BaselineDefaultsIncentivesFormClient
} from "api";
import RequirePermission from "../RequirePermission";
import { useRemoteData, responseHandlers } from "components/useRemoteData";
import { useApiEndpoint } from "components/useApiEndpoint";
import Spinner from "components/controls/Spinner";
import useMessageArea from "components/useMessageArea";
import BaselineDefaultsMenu from "../BaselineDefaultsMenu";
import DefaultsIncentivesForm from "../DefaultsIncentivesForm";
import utils from "utils";
import PrimaryButton from "components/controls/PrimaryButton";
import UnsavedChangesPrompt from "components/controls/UnsavedChangesPrompt";
import Spacer from "components/controls/Spacer";
import defaultsUtils, { IsChangeable } from "../defaultsUtils";
import VisibilityToggle from "components/controls/VisibilityToggle";

interface PageState {
  form: BaselineDefaultsIncentivesForm | undefined;
}

const BaselineDefaultsIncentivesPage: React.FC = function() {
  const [page, setPage] = useState<PageState>({
    form: undefined
  });

  const [
    messageAreaState,
    setMessageBasedOnForm,
    setMessageBasedOnApiException
  ] = useMessageArea();

  const setForm = function(form: BaselineDefaultsIncentivesForm | null) {
    if (!form) {
      return;
    }

    const clonedForm = utils.cloneObject(form);
    setPage({
      ...page,
      form: clonedForm
    });
  };

  const [dataState] = useRemoteData(
    useApiEndpoint(
      BaselineDefaultsIncentivesFormClient,
      "getBaselineDefaultsIncentivesForm"
    ),
    [],
    {
      autoFetch: true,
      onChange: form => {
        setForm(form);
      }
    }
  );

  const [submitState, submit] = useRemoteData(
    useApiEndpoint(
      BaselineDefaultsIncentivesFormClient,
      "saveBaselineDefaultsIncentivesForm"
    ),
    [page.form || {}],
    {
      onChange: form => {
        setMessageBasedOnForm(form);
        setForm(form);
      },
      onError: (error: ApiException) => {
        responseHandlers.use({
          "400": (ex: ApiException) => {
            const form = ex.result;
            setMessageBasedOnForm(form);
            setForm(form);
          },
          _: ex => {
            setMessageBasedOnApiException(ex);
          }
        })(error);
      }
    }
  );

  const spinner = (
    <Spinner isVisible={dataState.isFetching || submitState.isFetching} />
  );

  //See T-4258 for refactoring improvements
  if (page.form == null && (dataState.isFetching || submitState.isFetching)) {
    return spinner;
  } else if (page.form == null) {
    return messageAreaState.messageArea;
  }

  let isPageDirty =
    defaultsUtils.isFormChanged(page.form.selectedDivision as IsChangeable) ||
    defaultsUtils.areFormsChanged(page.form.divisions as IsChangeable[]);

  const actionArea = (
    <div style={{ display: "flex", alignItems: "center" }}>
      <UnsavedChangesPrompt isVisible={isPageDirty} />
      <Spacer orientation="h" size="xl" />
      <VisibilityToggle
        isVisible={
          page.form.pageConfiguration &&
          page.form.pageConfiguration.isEditEnabled
        }
      >
        <PrimaryButton
          disabled={submitState.isFetching}
          onClick={() => submit([page.form])}
          title="SAVE"
        />
      </VisibilityToggle>
    </div>
  );

  return (
    <Page menu={<BaselineDefaultsMenu actionArea={actionArea} />}>
      <RequirePermission
        permissions={["canViewBaselineDefaultsForms"]}
        accessDenied="page"
      >
        {spinner}
        {messageAreaState.messageArea}
        <DefaultsIncentivesForm
          isFetching={submitState.isFetching}
          submit={() => submit([page.form])}
          setForm={setForm}
          form={page.form}
          hideSaveButton={true}
          pageConfiguration={page.form.pageConfiguration}
        />
      </RequirePermission>
    </Page>
  );
};

export default BaselineDefaultsIncentivesPage;
